import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { IconChefHat } from '@tabler/icons-react';
import type { CachedRecipe, Rack, Recipe } from '@recipe-rack/shared';
import {
  callScrapeAndSummarise,
  createPendingRecipe,
  subscribeRacks,
  subscribeRecipes,
} from '@recipe-rack/shared';

import { db, functions } from '../Recipe/Firebase/InitialiseFirebase';
import { Chip, EmptyState, useToast } from '../../design/components';
import PasteBar from './PasteBar';
import RackRow from './RackRow';
import RecipeCard from './RecipeCard';
import TrendingRow from './TrendingRow';
import Shell from '../Layout/Shell';
import Sidebar from '../Layout/Sidebar';
import RecipeDrawer from '../Recipe/RecipeDrawer';
import ManageRacks from '../Rack/ManageRacks';

const RECENT_LIMIT = 8;

export interface LibraryProps {
  userId: string;
  userName?: string | null;
}

/** Pick the most-recently-added N recipes (excluding pending). */
function pickRecent(recipes: Recipe[], n: number): Recipe[] {
  // `recipeId` is a uuid so we can't sort lexicographically and pretend it's
  // chronological. Fall back to source order from Firestore (sorted by title),
  // which is good enough for the editorial "all recipes" preview until Phase B
  // teaches recipes to carry a `createdAt`.
  return recipes.filter((r) => r.version !== 0).slice(0, n);
}

const Library: React.FC<LibraryProps> = ({ userId, userName }) => {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [racks, setRacks] = useState<Rack[]>([]);
  const [selectedRecipeId, setSelectedRecipeId] = useState<string | null>(null);
  const [selectedRackId, setSelectedRackId] = useState<string | null>(null);
  const [manageRacksOpen, setManageRacksOpen] = useState(false);
  const { show } = useToast();

  /**
   * Save a trending recipe into the user's library. Goes through the existing
   * paste flow, which hits the cache and returns near-instantly.
   */
  const handleSaveCached = async (cached: CachedRecipe) => {
    const recipeId = uuidv4();
    try {
      await createPendingRecipe(db, userId, {
        recipeId,
        title: cached.title,
        ingredients: ['Saving from the rack…'],
        method: [],
        link: cached.url,
        version: 0,
      });
      await callScrapeAndSummarise(functions, {
        link: cached.url,
        recipeId,
        userId,
      });
      show(`Saved ${cached.title}`);
    } catch (err) {
      show(
        err instanceof Error ? err.message : 'Could not save recipe',
        'error'
      );
    }
  };

  useEffect(() => {
    const unsubRecipes = subscribeRecipes(db, userId, setRecipes);
    const unsubRacks = subscribeRacks(db, userId, setRacks);
    return () => {
      unsubRecipes();
      unsubRacks();
    };
  }, [userId]);

  const handleSelectRecipe = (id: string) => {
    setSelectedRecipeId((prev) => (prev === id ? null : id));
  };

  const selectedRecipe = useMemo(
    () => recipes.find((r) => r.recipeId === selectedRecipeId) ?? null,
    [recipes, selectedRecipeId]
  );

  const pendingRecipes = useMemo(
    () => recipes.filter((r) => r.version === 0),
    [recipes]
  );
  const realRecipes = useMemo(
    () => recipes.filter((r) => r.version !== 0),
    [recipes]
  );

  const recipesByRack = useMemo(() => {
    const map = new Map<string, Recipe[]>();
    racks.forEach((r) => map.set(r.rackId, []));
    realRecipes.forEach((r) => {
      (r.rackIds ?? []).forEach((id) => {
        if (!map.has(id)) return;
        map.get(id)!.push(r);
      });
    });
    return map;
  }, [racks, realRecipes]);

  const unfiledRecipes = useMemo(
    () => realRecipes.filter((r) => !r.rackIds || r.rackIds.length === 0),
    [realRecipes]
  );

  // Filtered view (single rack selected).
  const filteredRecipes = useMemo(() => {
    if (!selectedRackId) return realRecipes;
    if (selectedRackId === '__unfiled') return unfiledRecipes;
    return recipesByRack.get(selectedRackId) ?? [];
  }, [selectedRackId, realRecipes, recipesByRack, unfiledRecipes]);

  const sidebar = (
    <Sidebar
      userId={userId}
      userName={userName}
      racks={racks}
      recipes={recipes}
      selectedRackId={selectedRackId}
      onSelectRack={setSelectedRackId}
      onManageRacks={() => setManageRacksOpen(true)}
    />
  );

  const showEmptyState = recipes.length === 0;
  const showFilteredEmpty = !showEmptyState && filteredRecipes.length === 0 && selectedRackId !== null;

  return (
    <Shell sidebar={sidebar}>
      <div className="rr-library">
        <div className="rr-library-top">
          <div>
            <div className="rr-library-greet-eyebrow">Your library</div>
            <h1 className="rr-library-greet-title">
              What&rsquo;s <em>cooking</em>?
            </h1>
          </div>
        </div>

        {/* Mobile paste bar — shown when sidebar collapses. */}
        <div className="rr-library-mobile-paste">
          <PasteBar userId={userId} />
        </div>

        {/* Mobile chip strip — racks as a horizontal scroll. */}
        <div className="rr-library-chip-strip hide-scrollbar">
          <Chip
            selected={selectedRackId === null}
            onClick={() => setSelectedRackId(null)}
          >
            All
          </Chip>
          {racks.map((r) => (
            <Chip
              key={r.rackId}
              selected={selectedRackId === r.rackId}
              onClick={() => setSelectedRackId(r.rackId)}
            >
              {r.name}
            </Chip>
          ))}
          {unfiledRecipes.length > 0 && (
            <Chip
              selected={selectedRackId === '__unfiled'}
              onClick={() => setSelectedRackId('__unfiled')}
            >
              Unfiled
            </Chip>
          )}
        </div>

        {showEmptyState ? (
          <EmptyState
            icon={<IconChefHat size={48} stroke={1.4} />}
            title="No recipes yet"
            description="Paste a recipe URL above to start your library."
          />
        ) : selectedRackId ? (
          // Filtered view: flat 2-col grid.
          showFilteredEmpty ? (
            <EmptyState
              title="Nothing on this rack yet"
              description="Open a recipe and add this rack from the edit drawer."
            />
          ) : (
            <div className="rr-library-grid">
              {filteredRecipes.map((r) => (
                <RecipeCard
                  key={r.recipeId}
                  recipe={r}
                  racks={racks}
                  isOpen={selectedRecipeId === r.recipeId}
                  onClick={handleSelectRecipe}
                />
              ))}
            </div>
          )
        ) : (
          // Default: editorial rack rows.
          <div className="rr-library-rows">
            <TrendingRow onSave={handleSaveCached} />

            {pendingRecipes.length > 0 && (
              <RackRow
                title="In progress"
                recipes={pendingRecipes}
                racks={racks}
                selectedRecipeId={selectedRecipeId}
                onSelectRecipe={handleSelectRecipe}
              />
            )}

            <RackRow
              title="All recipes"
              recipes={pickRecent(realRecipes, RECENT_LIMIT)}
              racks={racks}
              seeAll={
                realRecipes.length > RECENT_LIMIT
                  ? { label: 'See all ›', onClick: () => setSelectedRackId(null) }
                  : undefined
              }
              selectedRecipeId={selectedRecipeId}
              onSelectRecipe={handleSelectRecipe}
            />

            {racks.map((rack) => {
              const items = recipesByRack.get(rack.rackId) ?? [];
              if (items.length === 0) return null;
              return (
                <RackRow
                  key={rack.rackId}
                  title={rack.name}
                  recipes={items}
                  racks={racks}
                  seeAll={{
                    label: 'See all ›',
                    onClick: () => setSelectedRackId(rack.rackId),
                  }}
                  selectedRecipeId={selectedRecipeId}
                  onSelectRecipe={handleSelectRecipe}
                />
              );
            })}

            {unfiledRecipes.length > 0 && (
              <RackRow
                title="Unfiled"
                recipes={unfiledRecipes}
                racks={racks}
                seeAll={{
                  label: 'See all ›',
                  onClick: () => setSelectedRackId('__unfiled'),
                }}
                selectedRecipeId={selectedRecipeId}
                onSelectRecipe={handleSelectRecipe}
              />
            )}
          </div>
        )}
      </div>

      <RecipeDrawer
        recipe={selectedRecipe}
        racks={racks}
        userId={userId}
        recipes={recipes}
        setRecipes={setRecipes}
        onClose={() => setSelectedRecipeId(null)}
      />

      <ManageRacks
        open={manageRacksOpen}
        onClose={() => setManageRacksOpen(false)}
        userId={userId}
        racks={racks}
      />
    </Shell>
  );
};

export default Library;
